import { Button, Divider, Stack } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../components/primitives/layout/PageContainer";
import { PageHeader } from "../../components/primitives/layout/PageHeader";
import { Section } from "../../components/primitives/layout/Section";
import { Panel } from "../../components/primitives/surfaces/Panel";
import { Body } from "../../components/primitives/data-display/Body";
import { Note } from "../../components/primitives/data-display/Note";
import { gaps } from "../../components/primitives/spacing";
import { WaitlistForm } from "../../components/domain/inputs/WaitlistForm";
import { CoverageRequestForm } from "./CoverageRequestForm";
import { ROUTES } from "../../route_paths";

/** Why we cannot check the claim, which sets the page's heading. */
export type NoCoverageReason = "health" | "outside_health";

interface HealthNoCoverageProps {
  reason: "health";
  claimText: string;
  /** Sends the request to be told when the claim becomes one we can check. */
  submitRequest: (email: string) => Promise<void>;
}

interface OutsideHealthNoCoverageProps {
  reason: "outside_health";
  claimText: string;
}

type NoCoverageProps = HealthNoCoverageProps | OutsideHealthNoCoverageProps;

/** Shown in place of a result for a claim we cannot check yet, with a way to be told when we can. */
export default function NoCoverage(props: NoCoverageProps) {
  const { t } = useTranslation();
  const isOutsideHealth = props.reason === "outside_health";

  return (
    <PageContainer>
      <PageHeader
        align="center"
        title={t(isOutsideHealth ? "noCoverage.outside_health.title" : "noCoverage.health.title")}
        subtitle={t(
          isOutsideHealth ? "noCoverage.outside_health.subtitle" : "noCoverage.health.subtitle"
        )}
      />

      {props.reason === "health" ? (
        <HealthRequest claimText={props.claimText} submitRequest={props.submitRequest} />
      ) : (
        <OutsideHealthWaitlist claimText={props.claimText} />
      )}

      <Stack sx={{ alignItems: "center" }}>
        <Button
          variant="outlined"
          component={Link}
          to={ROUTES.landing.path()}
          startIcon={<AddCircleOutlineIcon />}
        >
          {t("noCoverage.verifyAnother")}
        </Button>
      </Stack>
    </PageContainer>
  );
}

function ClaimPanel({ title, claimText }: { title: string; claimText: string }) {
  return (
    <Section title={title}>
      <Panel>
        <Body>{claimText}</Body>
      </Panel>
    </Section>
  );
}

/** The claim is health but not covered: ask for it to be looked into, then explain where coverage is heading. */
function HealthRequest({
  claimText,
  submitRequest,
}: {
  claimText: string;
  submitRequest: (email: string) => Promise<void>;
}) {
  const { t } = useTranslation();

  return (
    <>
      <CoverageRequestForm prompt={t("noCoverage.requestPrompt")} submitRequest={submitRequest} />

      <ClaimPanel title={t("noCoverage.claimShown")} claimText={claimText} />

      <Divider />

      <Stack gap={gaps.snug}>
        <Note>{t("noCoverage.expanding")}</Note>
        <Note>{t("noCoverage.roadmap")}</Note>
        <Note>{t("noCoverage.thanks")}</Note>
      </Stack>
    </>
  );
}

/** The claim is outside health: show it, then offer the waitlist for new topics. */
function OutsideHealthWaitlist({ claimText }: { claimText: string }) {
  const { t } = useTranslation();

  return (
    <>
      <ClaimPanel title={t("noCoverage.outside_health.claimShown")} claimText={claimText} />

      <Stack component="section" gap={gaps.base}>
        <Body>{t("noCoverage.outside_health.requestPrompt")}</Body>
        <WaitlistForm />
      </Stack>

      <Divider />

      <Note>{t("noCoverage.outside_health.expanding")}</Note>
    </>
  );
}
